import {
	ApplicationConfig,
	importProvidersFrom,
	inject,
	Injectable,
	LOCALE_ID,
	provideAppInitializer,
} from '@angular/core';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideRouter } from '@angular/router';
import { APP_ROUTES } from './app.routing';
import {
	MissingTranslationHandler,
	MissingTranslationHandlerParams,
	TranslateDefaultParser,
	TranslateLoader,
	TranslateModule,
	TranslateModuleConfig,
	TranslateParser,
} from '@ngx-translate/core';
import { HTTP_INTERCEPTORS, HttpBackend, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import {
	ACCOUNT_SETTINGS_CONFIG,
	ACCOUNT_SETTINGS_SERVICE_INJECTOR,
	AccountSettingsConfig,
	AlarisAccountSettingsService,
	AlarisApiService,
	AlarisAuthService,
	AlarisBalanceService,
	AlarisConfigService,
	AlarisDateFormatService,
	AlarisErrorInterceptor,
	AlarisFilesService,
	AlarisLanguageService,
	AlarisLocalStorageService,
	AlarisPasswordRestrictionsService,
	AlarisPaymentMethodsService,
	AlarisProfileService,
	AlarisResponseInterceptor,
	AlarisRpcInterceptor,
	AlarisSessionService,
	AlarisSignUpService,
	AlarisTableStateStore,
	AlarisToasterModule,
	AUTH_SERVICE_INJECTOR,
	BALANCE_SERVICE_INJECTOR,
	CAPTCHA_CONFIG,
	CaptchaConfig,
	CONFIG_SERVICE_INJECTOR,
	DATE_FILTER_END_OF_THE_DAY_INJECTOR,
	DATE_FORMAT_SERVICE_INJECTOR,
	DateFormatPipe,
	DAY_TYPE_HANDLER,
	English,
	ENVIRONMENT,
	FILES_SERVICE_INJECTOR,
	ICONS_CONFIG,
	LANGUAGE_CONFIG,
	LANGUAGE_SERVICE_INJECTOR,
	LanguageConfig,
	LocaleIdService,
	LOCALSTORAGE_SERVICE_INJECTOR,
	MENU_MQ_SERVICE_INJECTOR,
	MENU_MQ_WIDTH_VALUE,
	MenuSidebarService,
	PASSWORD_RESTRICTIONS_SERVICE_INJECTOR,
	PAYMENT_METHODS_SERVICE_INJECTOR,
	PROFILE_CONFIG,
	PROFILE_SERVICE_INJECTOR,
	ProfileConfig,
	REPO_SERVICE_INJECTOR,
	Russian,
	SESSION_SERVICE_INJECTOR,
	SIGN_IN_CONFIG,
	SIGN_IN_SERVICE_INJECTOR,
	SIGN_UP_CONFIG,
	SIGN_UP_SERVICE_INJECTOR,
	SignInConfig,
	SignUpConfig,
	Spain,
	TABLE_STATE_STORE_INJECTOR,
	Turkish,
} from '@campaign-portal/components-library';
import { EVENT_MANAGER_PLUGINS } from '@angular/platform-browser';
import { RoleType } from '@campaign-portal/namespace/common/enums';
import { environment } from '../environments/environment';
import { RepoService } from '@helpers/repo/repo.service';
import { AuthGuardService } from './auth/auth.guard';
import { DropAuthGuardService } from './auth/drop-auth.guard';
import { CanDeactivateGuardService } from '@helpers/shared/can-deactivate/can-deactivate.guard';
import { ActivateGuardService } from './auth/activate/activate.guard';
import { ResetGuardService } from './auth/reset/reset.guard';
import { MessageTemplatePipe, SenderPipe } from '@helpers/repo/repo.pipe';
import { DatePipe, registerLocaleData } from '@angular/common';
import { MultiTranslateHttpLoader } from 'ngx-translate-multi-http-loader';
import localeEn from '@angular/common/locales/en';
import localeEnExtra from '@angular/common/locales/extra/en';
import localeEs from '@angular/common/locales/es';
import localeEsExtra from '@angular/common/locales/extra/es';
import localeRu from '@angular/common/locales/ru';
import localeRuExtra from '@angular/common/locales/extra/ru';
import localeTr from '@angular/common/locales/tr';
import localeTrExtra from '@angular/common/locales/extra/tr';
import { ContentEditableDirective } from './pages/campaigns/shared/text-area-editor/helpers/content-editable.directive';
import { PreventEventPlugin } from '@taiga-ui/event-plugins'; /* Translations */

/* Translations */

export function HttpLoaderFactory(http: HttpBackend): MultiTranslateHttpLoader {
	return new MultiTranslateHttpLoader(http, [
		{ prefix: './assets/i18n_lib/app.', suffix: '.json?' + environment.version },
		{ prefix: './assets/i18n/app.', suffix: '.json?' + environment.version },
	]);
}

export class MissingTranslationService implements MissingTranslationHandler {
	handle(params: MissingTranslationHandlerParams): string {
		// @console.warn(`WARN: '${params.key}' is missing in '${params.translateService.currentLang}' locale`);
		return params.key;
	}
}

@Injectable()
export class TranslateParserService extends TranslateDefaultParser {
	override getValue(target: Record<string, unknown>, key: string): string | unknown {
		const result = super.getValue(target, key);
		return typeof result === 'object' && result !== null ? key : result;
	}
}

/* End of Translations */

/* forRoot() Configs */
const translateConfig: TranslateModuleConfig = {
	loader: {
		provide: TranslateLoader,
		useFactory: HttpLoaderFactory,
		deps: [HttpBackend],
	},
	missingTranslationHandler: {
		provide: MissingTranslationHandler,
		useClass: MissingTranslationService,
	},
	parser: { provide: TranslateParser, useClass: TranslateParserService },
	// if we set `defaultLanguage` to 'en' right here - we force loading en.json with translations
	// so missing translations will be used from en.file
	defaultLanguage: '',
};

const signInConfig: SignInConfig = {
	url: '',
	signInMethod: 'Auth.SignIn',
	refreshTokenMethod: 'Auth.Refresh',
	resetPasswordMethod: 'Auth.ResetPassword',
	signUpLink: '/sign-up',
	verify2FACodeMethod: 'Auth.Verify2FACode',
	is2FA: false,
	supportEmail: '',
};

const signUpConfig: SignUpConfig = {
	url: '',
	signInLink: '/sign-in',
	termsUrl: '',
	privacyUrl: '',
	supportEmail: '',
	signUpMethod: 'Auth.SignUp',
	registrationFieldsMethod: 'RegistrationFields.Read',
};

const captchaConfig: CaptchaConfig = {
	baseUrl: 'captcha',
};

const profileConfig: ProfileConfig = {
	profileRoute: '/account-settings',
	termsUrl: '',
	privacyUrl: '',
	supportEmail: '',
};

const accountSettingsConfig: AccountSettingsConfig = {
	resetPasswordMethod: 'Users.PersonalData.ResetPassword',
	validatePasswordMethod: 'PasswordRestrictions.PersonalData.Validate',
	userReadMethod: 'Users.PersonalData.Read',
	userUpdateMethod: 'Users.PersonalData.Update',
	partnersReadMethod: 'Partners.Read',
	verify2FACodeMethod: 'Auth.Verify2FACodeForReset',
	reset2FACodeMethod: 'Auth.GetQRForResetCodeForPortal',
	customFormsReadMethod: 'UserFields.Read',
	generatePasswordMethod: 'Users.GeneratePassword',
	role: RoleType.CAMPAIGN_USER,
	is2FA: false,
	showNoPermissionsWarning: true,
};

const languageConfig: LanguageConfig = { languages: [English, Spain], currentLanguage: English };
/* End of forRoot() Configs */

/* App Config */
export function loadConfig(): Promise<void> {
	const appConfig = inject(AlarisConfigService);
	const store = inject(AlarisLocalStorageService);
	const translate = inject(AlarisLanguageService);

	return appConfig.load().then(() => {
		signInConfig.url = appConfig.api;
		signInConfig.supportEmail = appConfig.supportEmail;
		signInConfig.is2FA = appConfig.is2FA;

		accountSettingsConfig.is2FA = appConfig.is2FA;

		signUpConfig.url = appConfig.api;
		signUpConfig.termsUrl = appConfig.termsUrl;
		signUpConfig.privacyUrl = appConfig.privacyUrl;
		signUpConfig.supportEmail = appConfig.supportEmail;

		profileConfig.termsUrl = appConfig.termsUrl;
		profileConfig.privacyUrl = appConfig.privacyUrl;
		profileConfig.supportEmail = appConfig.supportEmail;

		languageConfig.languages = appConfig.getLanguages();

		const lang = store.get('lang') ?? 'en';
		registerLocaleData(localeEn, English.id, localeEnExtra);
		registerLocaleData(localeEs, Spain.id, localeEsExtra);
		registerLocaleData(localeRu, Russian.id, localeRuExtra);
		registerLocaleData(localeTr, Turkish.id, localeTrExtra);
		// https://mcvendrell.medium.com/configuring-ngx-translate-to-load-at-startup-in-angular-1995e7dd6fcc
		translate.set(lang);
	});
}

/* End of App Config */

export const APP_CONFIG: ApplicationConfig = {
	providers: [
		importProvidersFrom(
			TranslateModule.forRoot(translateConfig),
			AlarisToasterModule.forRoot(),
			ContentEditableDirective
		),
		provideAnimations(),
		provideHttpClient(withInterceptorsFromDi()),
		provideRouter(APP_ROUTES),
		provideAppInitializer(loadConfig),

		AlarisConfigService,
		AlarisLocalStorageService,
		AlarisApiService,
		AlarisAuthService,
		AlarisSignUpService,
		AlarisProfileService,
		AlarisLanguageService,
		AlarisFilesService,
		AlarisBalanceService,
		AlarisPaymentMethodsService,
		AlarisAccountSettingsService,
		AlarisPasswordRestrictionsService,
		AlarisTableStateStore,
		AlarisSessionService,
		AlarisDateFormatService,

		{ provide: CAPTCHA_CONFIG, useValue: captchaConfig },
		{ provide: LANGUAGE_CONFIG, useValue: languageConfig },
		{ provide: PROFILE_CONFIG, useValue: profileConfig },
		{ provide: ICONS_CONFIG, useValue: { baseUrl: '/assets/icons' } },
		{ provide: ACCOUNT_SETTINGS_CONFIG, useValue: accountSettingsConfig },
		{ provide: SIGN_IN_CONFIG, useValue: signInConfig },
		{ provide: SIGN_UP_CONFIG, useValue: signUpConfig },
		{ provide: ENVIRONMENT, useValue: environment },
		{ provide: LOCALE_ID, useClass: LocaleIdService },

		{ provide: EVENT_MANAGER_PLUGINS, multi: true, useClass: PreventEventPlugin },
		{ provide: DAY_TYPE_HANDLER, useValue: (): string => 'weekday' },

		{ provide: LOCALSTORAGE_SERVICE_INJECTOR, useExisting: AlarisLocalStorageService },
		{ provide: CONFIG_SERVICE_INJECTOR, useExisting: AlarisConfigService },
		{ provide: REPO_SERVICE_INJECTOR, useExisting: RepoService },
		{ provide: AUTH_SERVICE_INJECTOR, useExisting: AlarisAuthService },
		{ provide: SIGN_IN_SERVICE_INJECTOR, useExisting: AlarisAuthService },
		{ provide: SIGN_UP_SERVICE_INJECTOR, useExisting: AlarisSignUpService },
		{ provide: PROFILE_SERVICE_INJECTOR, useExisting: AlarisProfileService },
		{ provide: LANGUAGE_SERVICE_INJECTOR, useExisting: AlarisLanguageService },
		{ provide: FILES_SERVICE_INJECTOR, useExisting: AlarisFilesService },
		{ provide: BALANCE_SERVICE_INJECTOR, useExisting: AlarisBalanceService },
		{ provide: PAYMENT_METHODS_SERVICE_INJECTOR, useExisting: AlarisPaymentMethodsService },
		{ provide: ACCOUNT_SETTINGS_SERVICE_INJECTOR, useExisting: AlarisAccountSettingsService },
		{ provide: PASSWORD_RESTRICTIONS_SERVICE_INJECTOR, useExisting: AlarisPasswordRestrictionsService },
		{ provide: TABLE_STATE_STORE_INJECTOR, useExisting: AlarisTableStateStore },
		{ provide: SESSION_SERVICE_INJECTOR, useExisting: AlarisSessionService },
		{ provide: DATE_FORMAT_SERVICE_INJECTOR, useExisting: AlarisDateFormatService },

		{ provide: HTTP_INTERCEPTORS, useClass: AlarisRpcInterceptor, multi: true },
		{ provide: HTTP_INTERCEPTORS, useClass: AlarisResponseInterceptor, multi: true },
		{ provide: HTTP_INTERCEPTORS, useClass: AlarisErrorInterceptor, multi: true },

		{ provide: DATE_FILTER_END_OF_THE_DAY_INJECTOR, useValue: true },
		{ provide: MENU_MQ_SERVICE_INJECTOR, useClass: MenuSidebarService },
		{ provide: MENU_MQ_WIDTH_VALUE, useValue: '(max-width: 1365px)' },

		AuthGuardService,
		DropAuthGuardService,
		CanDeactivateGuardService,
		ActivateGuardService,
		ResetGuardService,

		SenderPipe,
		MessageTemplatePipe,
		DatePipe,
		DateFormatPipe,
	],
};
